import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import { Form, Field } from 'react-final-form';
import omit from 'lodash.omit';
import { AutosaveLinkifiedTextarea, DatePicker, Select } from '@fikasio/react-ui-components';
import { clearPaneContent } from 'services/pane/slice';
import { fitTextareaHeight } from 'components/UI/fitTextareaHeight';
import ProjectsLabelsInput from 'components/projects/ProjectsLabelsInput';
import AssigneeButton from 'components/tasks/TasksViewer/AssigneeButton';
import TaskImportanceDropdown from 'components/tasks/TasksViewer/TaskImportanceDropdown';
import { useGetProjectsQuery } from 'services/projects/api';
import { useAddTaskMutation, useGetTaskQuery, usePatchTaskMutation, useUpdateTaskMutation } from 'services/tasks/api';
import { mutationFailed } from 'services/tasks/apiError';
import { Task, RecurrenceRule, TaskImportance } from 'services/tasks/types';
import { pickedDateTimeInUserZone, toJSDate, toPickerDate } from 'utils/date';
import { preserveEmptyString, processFormData } from 'utils/forms';
import { round } from 'utils/maths';
import TaskRecurrenceFields from './TaskRecurrenceFields';
import { taskEstimatedRemainingMinutes } from './timeLog';
import CompletionTimeEntryModal from 'components/tasks/CompletionTimeEntryModal';
import TaskStatusDropdown from 'components/tasks/TasksViewer/TaskStatusDropdown';
import { isNewlyCompleted, statusChangePatch, visibleStatus } from 'components/tasks/TasksViewer/statusChange';

interface TaskInformationsSlots {
  actions: React.ReactNode;
  details: React.ReactNode;
  metadata: React.ReactNode;
}

const HOURS_FROM_MINUTES = 119;

const completionTimeForForm = (minutes?: number) => {
  if (minutes == null) {
    return { units: 'minutes' as const, value: undefined };
  }
  if (minutes > HOURS_FROM_MINUTES) {
    return { units: 'hours' as const, value: round(minutes / 60, 2) };
  }
  return { units: 'minutes' as const, value: minutes };
};

interface TaskInformationsFormProps {
  children?: (slots: TaskInformationsSlots) => React.ReactNode;
  className?: string;
  descriptionRef?: React.RefObject<string>;
  onClose?: () => void;
  projectId?: string;
  task?: Task;
  taskDefaults?: Partial<Task>;
}

const TaskInformationsForm = ({
  children,
  className,
  descriptionRef,
  onClose,
  projectId,
  task,
  taskDefaults,
}: TaskInformationsFormProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { data: projects } = useGetProjectsQuery();
  const [startAt, setStartAt] = useState(
    toJSDate(task?.startAt) ?? null,
  );
  const [dueAt, setDueAt] = useState(
    toJSDate(task?.dueAt) ?? toJSDate(taskDefaults?.dueAt) ?? null,
  );
  const [recurrence, setRecurrence] = useState<RecurrenceRule[]>(
    task?.recurrence ?? [],
  );
  const [isCompletionEntryOpen, setIsCompletionEntryOpen] = useState(false);

  const seriesId = task?.type === 'Instance' ? task.taskId : undefined;
  const { data: parentTask } = useGetTaskQuery(seriesId ?? '', { skip: !seriesId });
  const series = parentTask?.recurrence?.length ? parentTask : undefined;

  const [createTask] = useAddTaskMutation();
  const [patchTask] = usePatchTaskMutation();
  const [updateTask] = useUpdateTaskMutation();

  const taskProjects = task
    ? task.projects
    : (taskDefaults?.projects?.length
      ? taskDefaults.projects
      : (projectId && projects?.find(p => p.id === projectId) ? [{ id: projectId }] : []));

  const remainingMinutes = taskEstimatedRemainingMinutes(task);
  const completionTime = completionTimeForForm(
    typeof remainingMinutes === 'number' ? remainingMinutes : undefined,
  );
  const detailsId = React.useId();
  const setDetailsRef = useCallback((element: HTMLTextAreaElement | null) => {
    if (element) {
      fitTextareaHeight(element);
    }
  }, []);

  useEffect(() => {
    setStartAt(toJSDate(task?.startAt) ?? null);
    setDueAt(
      toJSDate(task?.dueAt)
        ?? (task?.id ? null : toJSDate(taskDefaults?.dueAt))
        ?? null
    );
  }, [task?.id, task?.startAt, task?.dueAt]);

  useEffect(() => {
    if (task?.type === 'Instance') {
      setRecurrence(series?.recurrence ?? []);
      return;
    }
    setRecurrence(task?.recurrence ?? []);
  }, [series, task?.id, task?.recurrence, task?.type]);

  const onSubmit = async values => {
    const formData: any = processFormData(values);
    if (descriptionRef) {
      formData.description = descriptionRef.current;
    }
    if (formData.estimatedCompletionTime && formData.estimatedCompletionTimeUnits === 'hours') {
      formData.estimatedCompletionTime = Math.round(formData.estimatedCompletionTime * 60);
    }
    formData.projects = (formData.projects || []).filter(project => project?.id);
    formData.assignee = formData.assignee || null;
    if (!formData.importance) {
      if (task?.importance) {
        formData.importance = null;
      } else {
        delete formData.importance;
      }
    }
    const editingSeries = Boolean(series?.id);
    if (!editingSeries && task?.type !== 'Instance' && !task?.parentTaskId) {
      formData.recurrence = recurrence;
    }
    const taskFields = omit(formData, ['estimatedCompletionTimeUnits', 'timeEntries']);
    if (task && task.id) {
      const saves = [
        updateTask({
          id: task.id,
          ...taskFields,
        }),
      ];
      if (series?.id) {
        saves.push(patchTask({
          id: series.id,
          recurrence,
        }));
      }
      const results = await Promise.all(saves);
      if (!results.some(mutationFailed)) {
        dispatch(clearPaneContent());
      }
    } else {
      const result = await createTask({
        ...taskDefaults,
        ...taskFields,
      });
      if (!mutationFailed(result)) {
        dispatch(clearPaneContent());
      }
    }
  };

  return (
    <>
    <Form
      initialValues={{
        ...(task ?? {
          assignee: taskDefaults?.assignee,
          dueAt: dueAt ?? undefined,
          projects: taskProjects,
        }),
        ...(completionTime.value != null
          ? { 'number:estimatedCompletionTime': completionTime.value }
          : {}),
        estimatedCompletionTimeUnits: completionTime.units,
      }}
      keepDirtyOnReinitialize
      onSubmit={onSubmit}
      render={({ handleSubmit }) => {
        const details = (
          <div>
            <label className="form-label" htmlFor={detailsId}>{t('details')}</label>
            <Field
              name="details"
              parse={preserveEmptyString}
            >
              {
                ({ input }) => (
                  <AutosaveLinkifiedTextarea
                    id={detailsId}
                    name="details"
                    onBlur={input.onBlur}
                    onChange={event => {
                      input.onChange(event);
                      fitTextareaHeight(event.currentTarget);
                    }}
                    onFocus={input.onFocus}
                    onSave={details => {
                      if (task?.id) {
                        patchTask({
                          details,
                          id: task.id,
                        });
                      }
                    }}
                    ref={setDetailsRef}
                    value={input.value ?? ''}
                  />
                )
              }
            </Field>
          </div>
        );
        const metadata = (
          <>
          {
            task?.id && (
              <div>
                <label className="form-label">{t('status')}</label>
                <div>
                  <TaskStatusDropdown
                    alignToggle="start"
                    onChange={(status, extras) => {
                      patchTask({
                        id: task.id,
                        ...statusChangePatch(status, {
                          cancelReason: extras?.cancelReason,
                          wasArchived: task.isArchived,
                        }),
                      });
                      if (isNewlyCompleted(task.status, status)) {
                        setIsCompletionEntryOpen(true);
                      }
                    }}
                    status={visibleStatus(task.status, task.isArchived)}
                  />
                </div>
                {
                  task.status === 'Cancelled' && task.cancelReason && (
                    <div
                      style={{
                        color: '#6c757d',
                        marginTop: 8,
                      }}
                    >
                      {t('cancelReason')}: {task.cancelReason}
                    </div>
                  )
                }
              </div>
            )
          }
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
            }}
          >
            <div style={{ flex: '1 1 140px', minWidth: 0 }}>
              <label className="form-label">{t('startDate')}</label>
              <Field
                component={
                  ({ input }) => (
                    <DatePicker
                      className="form-control"
                      defaultValue={toPickerDate(startAt)}
                      displayFormat="yyyy-MM-dd"
                      key={startAt ? 'has-start-at' : 'no-start-at'}
                      name="startAt"
                      onChange={value => {
                        const timestamp = pickedDateTimeInUserZone(value)
                          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
                        if (task && task.id) {
                          patchTask({
                            id: task.id,
                            startAt: timestamp.toJSDate(),
                          });
                        }
                        setStartAt(timestamp.toJSDate());
                        input.onChange(timestamp.toJSDate());
                      }}
                      onRemoveValue={e => {
                        e.stopPropagation();
                        if (task && task.id) {
                          patchTask({
                            id: task.id,
                            startAt: null,
                          });
                        }
                        setStartAt(null);
                        input.onChange(null);
                      }}
                      showRemoveValue
                      showTimeSelect={false}
                      value={toPickerDate(startAt)}
                    />
                  )
                }
                defaultValue={startAt ?? undefined}
                name="startAt"
              />
            </div>
            <div style={{ flex: '1 1 140px', minWidth: 0 }}>
              <label className="form-label">{t('dueDate')}</label>
              <Field
                component={
                  ({ input }) => (
                    <DatePicker
                      className="form-control"
                      defaultValue={toPickerDate(dueAt)}
                      displayFormat="yyyy-MM-dd"
                      key={dueAt ? 'has-due-at' : 'no-due-at'}
                      name="dueAt"
                      onChange={value => {
                        const timestamp = pickedDateTimeInUserZone(value)
                          .set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
                        if (task && task.id) {
                          patchTask({
                            id: task.id,
                            dueAt: timestamp.toJSDate(),
                          });
                        }
                        setDueAt(timestamp.toJSDate());
                        input.onChange(timestamp.toJSDate());
                      }}
                      onRemoveValue={e => {
                        e.stopPropagation();
                        if (task && task.id) {
                          patchTask({
                            id: task.id,
                            dueAt: null,
                          });
                        }
                        setDueAt(null);
                        input.onChange(null);
                      }}
                      showRemoveValue
                      showTimeSelect={false}
                      value={toPickerDate(dueAt)}
                    />
                  )
                }
                defaultValue={dueAt ?? undefined}
                name="dueAt"
              />
            </div>
          </div>
          <div>
            <label className="form-label">{t('estimatedCompletionTime')}</label>
            <div
              style={{
                display: 'flex',
                gap: 8,
              }}
            >
              <Field
                className="form-control"
                component="input"
                initialValue={completionTime.value}
                name="number:estimatedCompletionTime"
                style={{
                  flex: '0 0 88px',
                  width: 88,
                }}
              />
              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                <Field
                  component={
                    ({ input }) => {
                      return (
                        <Select
                          onChange={value => input.onChange(value)}
                          options={[
                            { label: t('minutes'), value: 'minutes' },
                            { label: t('hours'), value: 'hours' },
                          ]}
                          style={{
                            width: '100%',
                          }}
                          value={input.value || 'minutes'}
                        />
                      )
                    }
                  }
                  initialValue={completionTime.units}
                  name="estimatedCompletionTimeUnits"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="form-label">{t('assignee')}</label>
            <Field name="assignee">
              {({ input }) => (
                <AssigneeButton
                  assigneeId={input.value || undefined}
                  onChange={assignee => {
                    input.onChange(assignee);
                    if (task && task.id) {
                      patchTask({
                        id: task.id,
                        assignee,
                      });
                    }
                  }}
                />
              )}
            </Field>
          </div>
          <div>
            <label className="form-label">{t('projects')}</label>
            <Field
              defaultValue={taskProjects}
              name="projects"
            >
              {({ input }) => (
                <ProjectsLabelsInput
                  onChange={nextProjects => {
                    const projectsToSave = (nextProjects || []).filter(project => project?.id);
                    input.onChange(projectsToSave);
                    if (task?.id) {
                      patchTask({
                        id: task.id,
                        projects: projectsToSave,
                      });
                    }
                  }}
                  projects={projects}
                  value={input.value}
                />
              )}
            </Field>
          </div>
          <div>
            <label className="form-label">{t('importance')}</label>
            <Field name="importance">
              {({ input }) => (
                <TaskImportanceDropdown
                  alignToggle="start"
                  importance={(input.value || null) as TaskImportance | null}
                  onChange={importance => {
                    input.onChange(importance);
                    if (task?.id) {
                      patchTask({
                        id: task.id,
                        importance,
                      });
                    }
                  }}
                />
              )}
            </Field>
          </div>
          {
            ((task?.type !== 'Instance' && !task?.parentTaskId) || series) && (
              <TaskRecurrenceFields
                anchorDate={dueAt ?? startAt}
                key={task?.id ?? 'new'}
                onChange={setRecurrence}
                recurrence={recurrence}
              />
            )
          }
          </>
        );
        const actions = (
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
            }}
          >
            <Button
              onClick={() => onClose && onClose()}
              style={{
                backgroundColor: 'white',
              }}
              variant="outline-secondary"
            >
              {t('cancel')}
            </Button>
            {
              task
                ? <Button type="submit" variant="success">{t('save')}</Button>
                : <Button type="submit" variant="success">{t('create')}</Button>
            }
          </div>
        );
        return (
          <form
            className={className}
            onKeyDown={event => {
              if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
                event.preventDefault();
              }
            }}
            onSubmit={handleSubmit}
          >
            {
              children
                ? children({ actions, details, metadata })
                : (
                  <>
                    {details}
                    {metadata}
                    {actions}
                  </>
                )
            }
          </form>
        );
      }}
    />
    {
      isCompletionEntryOpen && task?.id && (
        <CompletionTimeEntryModal
          onHide={() => setIsCompletionEntryOpen(false)}
          onSave={patch => {
            patchTask({
              id: task.id,
              ...patch,
            });
          }}
          task={task}
        />
      )
    }
    </>
  );
};

export default TaskInformationsForm;
